import { useState, type FormEvent } from 'react';
import { useAuth } from './AuthContext';
import type { Theme } from '../theme';
import { DevCredentials } from './DevCredentials';
import { Icon } from '../shells/Icon';

const fieldStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 11px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--rounded-md)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
};

/**
 * Deep Navy Login surface (UX-DR10). Posts email/password to the API auth
 * broker via `useAuth().signIn`. On failure it renders the server's generic
 * message verbatim in an aria-live region — the SPA adds NO "email not found"
 * hinting (FR-2, AC2). Focus ring + accessible labels come from the token layer.
 *
 * The theme toggle is the prototype's (`reconcile-prototype.md` #31 puts it on "topbar +
 * login"). It matters more here than anywhere else in the app: this is the one surface a
 * signed-out user can reach, so there is no `/me/settings` to restore a stored preference
 * from — without a control here, someone who needs the dark theme cannot get it until after
 * they have already read and filled a light-theme form.
 *
 * Both props are optional so the many tests that render a bare `<Login />` keep working; the
 * control simply does not render when the owner does not pass a handler.
 */
export function Login({
  theme,
  onToggleTheme,
}: {
  theme?: Theme;
  onToggleTheme?: () => void;
} = {}): React.JSX.Element {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    // Form is noValidate — guard empty fields here instead of round-tripping.
    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }
    setSubmitting(true);
    try {
      await signIn({ email, password });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        // `place-items` sets align-ITEMS, not align-CONTENT: without this the two
        // implicit rows stretch to fill 100vh and the dev panel drifts far below
        // the card. No-op when DevCredentials renders null (single centered row).
        alignContent: 'center',
        gap: 14,
        background: 'var(--app-bg)',
        padding: 'var(--gutter)',
      }}
    >
      <section
        className="card stack"
        aria-labelledby="login-heading"
        // DESIGN.md gives the login card its OWN radius — `rounded.xxl` (18px) — rather than
        // the 13px `rounded.lg` every other card uses. `.card` supplies the latter, so the
        // one surface the spec singles out has to override it.
        style={{ width: 'min(400px, 100%)', borderRadius: 'var(--rounded-xxl)' }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div className="stack" style={{ gap: 4 }}>
            <h1 id="login-heading" style={{ margin: 0, fontSize: 21, fontWeight: 800 }}>
              Vendor Compliance
            </h1>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              Sign in to your account
            </p>
          </div>
          {onToggleTheme && (
            // Deliberately identical to the in-shell toggle (ShellLayout) — same 38px icon
            // button, same destination-not-state glyph, same accessible name. This is the one
            // theme control a signed-out user can reach, and the UAT sweep drives login and
            // the shells through a SINGLE `setTheme()` helper matching one name pattern; two
            // shapes here would mean two patterns, and the divergence would only show up as a
            // failing screenshot sweep.
            <button
              type="button"
              className="icon-button"
              data-icon-only="true"
              onClick={onToggleTheme}
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
            >
              <Icon name={theme === 'light' ? 'moon' : 'sun'} size={18} />
            </button>
          )}
        </div>

        <form className="stack" onSubmit={onSubmit} noValidate>
          <div className="stack" style={{ gap: 6 }}>
            <label className="field-label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={fieldStyle}
            />
          </div>

          <div className="stack" style={{ gap: 6 }}>
            <label className="field-label" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={fieldStyle}
            />
          </div>

          <div role="alert" aria-live="assertive" style={{ minHeight: 18 }}>
            {error && (
              <span className="status-pill" data-tone="bad">
                <span className="status-pill__dot" aria-hidden="true" />
                {error}
              </span>
            )}
          </div>

          <button
            type="submit"
            className="btn-accent"
            data-surface="accent"
            disabled={submitting}
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </section>

      <DevCredentials
        onPick={({ email: pickedEmail, password: pickedPassword }) => {
          setEmail(pickedEmail);
          setPassword(pickedPassword);
          // A stale "invalid credentials" next to freshly filled fields reads as
          // if the pick itself failed.
          setError(null);
        }}
      />
    </div>
  );
}
